// Plan 08-02 — typed fetch wrapper.
// Plan 14-07 — MUI-08 silent-refresh single-retry on 401.
//
// Auto-attaches:
//   - Accept: application/vnd.runegambit.v1+json (DEC-018, every request)
//   - Authorization: Bearer ${jwt}                (effectiveBearerJwt: real
//     $authBearer if set, else the anon practice JWT — ARCA-93)
//   - Content-Type: application/vnd.runegambit.v1+json (when body present)
//   - Idempotency-Key: ${init.idempotencyKey}     (when caller supplied)
//
// Error handling:
//   - 401 (attempt 0):    trySilentRefresh; on success wait 500ms + retry
//                         ONCE with the rotated bearer (MUI-08 / Plan 14-07).
//                         buildHeaders re-reads $authBearer on the retry so
//                         the rotated JWT is automatically picked up.
//   - 401 (attempt 1 or refresh failed):
//                         clearAuth() + throw AuthInvalidError (oracle-
//                         hardened — no distinguishing of sub-cause).
//                         Refresh failure (429/5xx/IdP-401/no-bearer) is
//                         treated as immediate clearAuth per Open-Question 4
//                         resolution in 14-RESEARCH.
//   - 5xx (attempt 0):    ONE retry after 250ms with the SAME Idempotency-Key
//                         (RFC 9457 transient classification). Does NOT alter
//                         the key on retry.
//   - other:              throw ApiError(status, body).
//
// Network errors (fetch rejects): treated as 5xx-ish (one retry, then throw
// ApiError(0)).
//
// The 401-refresh-retry and the 5xx-retry SHARE the `attempt` counter — a
// single apiFetch invocation retries AT MOST ONCE regardless of which branch
// triggered it. (T-14-07-01 mitigation: bounded refresh-retry storm.)
//
// Path typing: callers pass keys of `paths` from @runegambit/api-types. The
// signature stays generic here so Plan 04+05 can wire strict path typing
// when they import the typed paths object directly.

import type { paths } from '@runegambit/api-types'

import { forceSilentRefresh } from '../auth/silent-refresh.js'
import { AuthInvalidError } from '../auth/types.js'
import { navigateTo } from '../router/nav.js'
import { clearAnonSession, effectiveBearerJwt } from '../stores/anonSession.js'
import { $authBearer, clearAuth } from '../stores/auth.js'

const V1 = 'application/vnd.runegambit.v1+json'
const RETRY_BACKOFF_MS = 250
// MUI-08 (Plan 14-07): 500ms — distinct from the 5xx-retry shape (250ms). The
// extra latency budget covers the silent-refresh round-trip jitter without
// stretching the visible "your turn was slow" tail.
const REFRESH_RETRY_BACKOFF_MS = 500

// ApiPath: keys of the OpenAPI-generated `paths` map. Concrete callers
// (e.g. /api/auth/exchange, /api/match/{id}/turn) get autocomplete + literal
// path-string typechecking via this constraint.
export type ApiPath = keyof paths

export interface ApiFetchInit {
  body?: unknown
  headers?: Record<string, string>
  idempotencyKey?: string
  signal?: AbortSignal
}

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown, message = `API error ${status}`) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

function getApiBaseUrl(): string {
  const url = import.meta.env?.VITE_API_BASE_URL
  if (!url || url.startsWith('<')) {
    throw new ApiError(0, null, 'VITE_API_BASE_URL not configured')
  }
  return url
}

function buildHeaders(
  method: string,
  init: ApiFetchInit | undefined,
  hasBody: boolean,
): Headers {
  const headers = new Headers(init?.headers)
  headers.set('Accept', V1)
  if (hasBody) headers.set('Content-Type', V1)
  if (init?.idempotencyKey) headers.set('Idempotency-Key', init.idempotencyKey)

  // ARCA-93: prefer the real authenticated bearer; fall back to the anon
  // practice JWT (effectiveBearerJwt) so guest-OK bot-match routes work with
  // zero sign-in. The authenticated path is unchanged (real bearer wins).
  const jwt = effectiveBearerJwt()
  if (jwt) headers.set('Authorization', `Bearer ${jwt}`)

  // Method header is informational only — leave to caller to set when needed.
  void method
  return headers
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => '')
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function rawFetch(
  url: string,
  method: string,
  init: ApiFetchInit | undefined,
  hasBody: boolean,
): Promise<Response> {
  const fetchInit: RequestInit = {
    method,
    headers: buildHeaders(method, init, hasBody),
  }
  if (hasBody) fetchInit.body = JSON.stringify(init?.body)
  if (init?.signal) fetchInit.signal = init.signal
  return fetch(url, fetchInit)
}

async function delay(ms: number): Promise<void> {
  await new Promise<void>((resolve) => setTimeout(resolve, ms))
}

/**
 * MUI-08 (Plan 14-07): attempt a silent IdP-driven bearer rotation. Used by
 * the apiFetch 401 branch BEFORE giving up on the session.
 *
 * Returns `true` if `$authBearer` was rotated to a fresh JWT — the caller
 * then waits `REFRESH_RETRY_BACKOFF_MS` and retries the original request
 * (`buildHeaders` re-reads `$authBearer` so the new JWT is picked up
 * automatically).
 *
 * Returns `false` on:
 *   - no bearer in the atom (user is signed out; nothing to refresh)
 *   - IdP refused silent re-auth
 *   - `/api/auth/exchange` returned a non-200 status. Per Open-Question 4
 *     (14-RESEARCH.md), 429 from the refresh endpoint is treated as failure
 *     — we do NOT retry and we do NOT distinguish 429 from other failures
 *     to the caller; both clear auth.
 *   - network error during the IdP/exchange round-trip
 *
 * Side effect on success: $authBearer is updated via `saveAuth` inside the
 * underlying `forceSilentRefresh` helper.
 */
async function trySilentRefresh(): Promise<boolean> {
  if ($authBearer.get() === null) return false
  try {
    return await forceSilentRefresh()
  } catch {
    // AuthLockedError (423) or unexpected error: caller's perspective is the
    // same as "refresh failed" — clearAuth + AuthInvalidError. The
    // AuthLockedError-specific UI is surfaced by the AuthProvider boundary
    // once it observes the cleared atom.
    return false
  }
}

/**
 * ARCA-94 / WS3 — recover from a terminal auth loss by routing to /splash.
 *
 * A terminal 401 (silent-refresh failed or already retried once) means the
 * session that the request presented — the real bearer OR the anon practice
 * JWT — is dead. The caller above has just `clearAuth()` + `clearAnonSession()`
 * to wipe both atoms, but that alone leaves the user sitting on a now-
 * unauthorized screen that simply keeps 401ing. The router-level guard
 * (ARCA-92 / WS1) only bounces `access: 'auth'` routes, so a guest stranded on
 * a `guest`-access surface (a bot-practice /match or /replay whose anon JWT
 * expired) would NOT be rescued by the guard. Sending them to /splash — the
 * canonical signed-out home (sign-in + bot practice) — guarantees no path ever
 * dead-ends a guest. This mirrors `AuthProvider.logout()`, which also routes to
 * /splash on session teardown.
 *
 * `navigateTo` is the module-level router bridge (router/nav.ts), so this works
 * from non-React net code. Skip the redundant push when already on /splash
 * (e.g. an anon-mint 401 fired from the splash bot-practice CTA) so we never
 * stack duplicate history entries.
 */
function recoverToSplashOnAuthLoss(): void {
  if (typeof window !== 'undefined' && window.location.pathname === '/splash') {
    return
  }
  navigateTo('/splash')
}

export async function apiFetch<T = unknown>(
  path: string,
  method: string,
  init?: ApiFetchInit,
): Promise<T> {
  const url = `${getApiBaseUrl()}${path}`
  const hasBody =
    init?.body !== undefined && method !== 'GET' && method !== 'HEAD'

  let response: Response
  let attempt = 0
  // First attempt + at most one retry on 5xx / network failure.
  // ──────────────────────────────────────────────────────────────────────
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      response = await rawFetch(url, method, init, hasBody)
    } catch {
      // Network error — treat as transient, retry once.
      if (attempt === 0) {
        attempt += 1
        await delay(RETRY_BACKOFF_MS)
        continue
      }
      throw new ApiError(0, null, `Network error on ${method} ${path}`)
    }

    if (response.status === 401) {
      // MUI-08 (Plan 14-07) — silent-refresh single-retry @ 500ms before
      // clearing auth. Refresh failure (429/5xx/no-token) → immediate
      // clearAuth. On attempt 0 only: try refreshing the bearer in-band; if
      // the IdP/exchange path rotates the atom, wait 500ms and retry the
      // original request once. The retry's headers are rebuilt from scratch
      // so the rotated JWT and the original Idempotency-Key both flow.
      if (attempt === 0) {
        const refreshed = await trySilentRefresh()
        if (refreshed) {
          attempt += 1
          await delay(REFRESH_RETRY_BACKOFF_MS)
          continue
        }
      }
      // Drop the body unread — oracle-hardened ErrorEnvelope is byte-equal.
      // Clear BOTH bearers: the request presented whichever was effective
      // (real or anon) and the server rejected it, so neither is usable.
      clearAuth()
      clearAnonSession()
      // ARCA-94 / WS3: both bearers are now gone — route to /splash so a stray
      // 401 never strands a guest on a dead screen (the guard alone misses
      // guest-access surfaces). Then surface the error to the caller as before.
      recoverToSplashOnAuthLoss()
      throw new AuthInvalidError()
    }

    if (response.status >= 500 && attempt === 0) {
      attempt += 1
      await delay(RETRY_BACKOFF_MS)
      continue
    }

    if (response.status >= 400) {
      const body = await readBody(response)
      throw new ApiError(response.status, body)
    }

    // 2xx success
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
  }
}
